import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../../Input/Button/Button';
import { ROUTES } from '../../../../utils/routes';
// eslint-disable-next-line import/no-cycle
import {
  filterDealerLaunchSections,
  getNextIncompleteStep,
  getNextSection,
  shouldHideSectionOrStep,
} from '../../steps';
import { DealerLaunchSectionProps } from '../../types';
import { StatusList } from '../../../StatusList/StatusList';
import styles from '../../styles.module.scss';
import componentStyles from './styles.module.scss';

export const DealerLaunchSectionOverview: FunctionComponent<DealerLaunchSectionProps> = ({
  section,
  dealer,
}) => {
  const history = useHistory();

  // Used because some steps have filters in addition to the section filters
  const stepsToShow = useMemo(
    () =>
      section?.steps
        .filter((s) => (s.shouldHideStep ? !s.shouldHideStep(dealer) : true))
        .filter((step) => !shouldHideSectionOrStep(step, dealer)),
    [section, dealer],
  );
  const completionStepIndex = useMemo<number>(() => {
    const index = getNextIncompleteStep(section, dealer, stepsToShow);
    return index || 0;
  }, [section, dealer, stepsToShow]);

  const sectionIsComplete = useMemo(() => {
    const steps = stepsToShow;
    const stepsLength = steps?.length;
    return completionStepIndex === stepsLength;
  }, [completionStepIndex, stepsToShow]);

  const nextSection = useMemo(
    () =>
      getNextSection({
        currentSection: section,
        dealer,
        filteredSections: filterDealerLaunchSections(dealer),
      }),
    [section, dealer],
  );

  const nextCompletionStep = useCallback(() => {
    const steps = stepsToShow;
    if (!sectionIsComplete) {
      const nextStep = steps[completionStepIndex];
      history.push(nextStep.route.getPath(dealer));
    } else if (!nextSection) {
      history.push(ROUTES.DEALER_DASHBOARD_REVIEW.getPath());
    } else {
      history.push(nextSection.route.getPath(dealer));
    }
  }, [
    history,
    dealer,
    completionStepIndex,
    sectionIsComplete,
    nextSection,
    stepsToShow,
  ]);

  const getCompletionLabel = useMemo(() => {
    if (sectionIsComplete && !nextSection) {
      return 'Continue to Review';
    } else if (sectionIsComplete) {
      return 'Next Section';
    }
    return 'Continue Setup';
  }, [nextSection, sectionIsComplete]);

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const renderNav = () => (
    <div className={styles.pageNav}>
      <Button
        className={styles.pageNavButton}
        label="Previous"
        onClick={goBack}
      />
      <div className="spacer" />
      <Button
        className={styles.pageNavButton}
        label={getCompletionLabel}
        variant="primary"
        onClick={nextCompletionStep}
      />
    </div>
  );

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepTitle}>{section.name}</div>
      <p className={styles.stepSubtitle}>
        Here&apos;s a quick overview of your progress in this section.
        <br />
        Click on any of the steps to go back and view or edit your information.
      </p>
      <div className={styles.stepCard} id={componentStyles.steps}>
        <h1 className={styles.stepCardTitle}>Your Progress</h1>
        <StatusList
          items={stepsToShow.map(
            (
              { name, route: stepRoute, isComplete, description, hideStepNav },
              index,
            ) => ({
              label: (
                <>
                  {name}
                  <div className={componentStyles.stepDescription}>
                    <p>{description}</p>
                    {index === completionStepIndex && (
                      <div className={componentStyles.stepCompletion}>
                        Continue from here
                      </div>
                    )}
                  </div>
                </>
              ),
              complete: isComplete(dealer),
              to: stepRoute.getPath(dealer),
              hide: !!hideStepNav,
            }),
          )}
        />
      </div>
      {renderNav()}
    </div>
  );
};
